import { useEffect, useState } from "react";
import { Menu, X, Globe, Calendar } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { BOOKING } from "../i18n";
import { openCalendly } from "../utils/calendly";

const LANGS = [
  { code: "es", label: "ES" },
  { code: "en", label: "EN" },
  { code: "zh", label: "中" },
];

export default function Navbar({ lang, setLang, t }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // When on /grupal, anchor links must take user back to the home page
  const anchor = (id) => (isHome ? `#${id}` : `/#${id}`);

  const links = [
    { href: anchor("therapies"), label: t.nav.therapies },
    { href: anchor("session"), label: t.nav.session },
    { href: anchor("process"), label: t.nav.process },
    { to: "/grupal", label: t.nav.group, route: true },
    { href: anchor("about"), label: t.nav.about },
    { href: anchor("contact"), label: t.nav.contact },
  ];

  const handleBook = (e) => {
    e?.preventDefault();
    setOpen(false);
    openCalendly(BOOKING.calendlyDiscovery);
  };

  return (
    <>
      <header
        data-testid="site-navbar"
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
          scrolled
            ? "bg-[#FDFBF7]/90 backdrop-blur-xl border-b border-[#E8E1D9]/70 py-4"
            : "bg-transparent py-6"
        }`}
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 flex items-center justify-between">
          <Link
            to="/"
            data-testid="nav-logo"
            className="flex items-baseline gap-2 group"
          >
            <span className="font-serif text-2xl md:text-3xl font-light tracking-tight text-[#3E362E]">
              Danwei Huang
            </span>
            <span className="font-chinese text-lg text-[#1A9BC1] opacity-80 group-hover:opacity-100 transition-opacity">
              源
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-9">
            {links.map((l) =>
              l.route ? (
                <Link
                  key={l.to}
                  to={l.to}
                  className="nav-link"
                  data-testid={`nav-${l.to.replace("/", "")}`}
                >
                  {l.label}
                </Link>
              ) : (
                <a
                  key={l.href}
                  href={l.href}
                  className="nav-link"
                  data-testid={`nav-${l.href.replace(/^[/#]+/, "")}`}
                >
                  {l.label}
                </a>
              ),
            )}
          </nav>

          <div className="flex items-center gap-4">
            <div className="hidden md:flex items-center border border-[#E8E1D9] divide-x divide-[#E8E1D9]">
              {LANGS.map((l) => (
                <button
                  key={l.code}
                  onClick={() => setLang(l.code)}
                  className={`lang-btn ${lang === l.code ? "active" : ""}`}
                  data-testid={`lang-${l.code}`}
                  aria-label={`Switch to ${l.label}`}
                >
                  {l.label}
                </button>
              ))}
            </div>

            <button
              type="button"
              onClick={handleBook}
              className="hidden md:inline-flex btn-accent"
              data-testid="nav-cta-book"
            >
              <Calendar size={14} />
              {t.nav.book}
            </button>

            <button
              className="lg:hidden p-2"
              onClick={() => setOpen(true)}
              aria-label="Open menu"
              data-testid="mobile-menu-open"
            >
              <Menu size={22} />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile menu */}
      <div className={`mobile-menu ${open ? "open" : ""} lg:hidden`}>
        <button
          className="absolute top-6 right-6 p-2"
          onClick={() => setOpen(false)}
          aria-label="Close menu"
          data-testid="mobile-menu-close"
        >
          <X size={26} />
        </button>
        <nav className="flex flex-col gap-8">
          {links.map((l) =>
            l.route ? (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                className="font-serif text-4xl text-[#3E362E]"
                data-testid={`mobile-nav-${l.to.replace("/", "")}`}
              >
                {l.label}
              </Link>
            ) : (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setOpen(false)}
                className="font-serif text-4xl text-[#3E362E]"
                data-testid={`mobile-nav-${l.href.replace(/^[/#]+/, "")}`}
              >
                {l.label}
              </a>
            ),
          )}
        </nav>

        <div className="mt-12 flex items-center gap-2 border border-[#E8E1D9] w-fit">
          <Globe size={16} className="ml-3 text-[#7D746A]" />
          {LANGS.map((l) => (
            <button
              key={l.code}
              onClick={() => setLang(l.code)}
              className={`lang-btn ${lang === l.code ? "active" : ""}`}
              data-testid={`mobile-lang-${l.code}`}
            >
              {l.label}
            </button>
          ))}
        </div>

        <button
          type="button"
          onClick={handleBook}
          className="mt-8 btn-accent w-fit"
          data-testid="mobile-nav-cta-book"
        >
          <Calendar size={14} />
          {t.nav.book}
        </button>
      </div>
    </>
  );
}
